import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '../custom-components';
import { Device, Connection, ApplicationEntity } from '../types';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'device' | 'connection' | 'ae';
  item: any;
  onDelete: () => void;
}

export const DeleteConfirmationModal: React.FC<
  DeleteConfirmationModalProps
> = ({ isOpen, onClose, type, item, onDelete }) => {
  const { t } = useTranslation('Organization');

  const getItemName = () => {
    if (!item) return '';

    switch (type) {
      case 'device':
        return (item as Device).name;
      case 'connection':
        return item.connection?.name || '';
      case 'ae':
        return (item as ApplicationEntity).aeTitle;
      default:
        return '';
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'device':
        return t('devices.modals.deleteDeviceTitle');
      case 'connection':
        return t('devices.modals.deleteConnectionTitle');
      case 'ae':
        return t('devices.modals.deleteAeTitle');
      default:
        return t('devices.modals.deleteTitle');
    }
  };

  const getDescription = () => {
    const itemName = getItemName();
    switch (type) {
      case 'device':
        return t('devices.modals.deleteDeviceDescription', { name: itemName });
      case 'connection':
        return t('devices.modals.deleteConnectionDescription', { name: itemName });
      case 'ae':
        return t('devices.modals.deleteAeDescription', { name: itemName });
      default:
        return t('devices.modals.deleteGenericDescription');
    }
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      description={getDescription()}
    >
      <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
        <span className="text-sm text-red-800 dark:text-red-400 font-medium">
          {t('devices.modals.cannotUndo')}
        </span>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button variant="destructive" onClick={handleDelete}>
          {t('devices.modals.delete')}
        </Button>
      </div>
    </Modal>
  );
};
